import { isSupabaseConfigured, supabase } from './supabaseClient';

const emptyData = {
    categories: { expenses: [], income: [], goals: [] },
    transactions: { expenses: [], income: [], savings: [] },
    notes: []
};

const normalizeId = (id) => String(id);

const mapCategoryFromDb = (row) => ({
    id: row.id,
    name: row.name,
    ...(row.type === 'expenses' && { limit: Number(row.limit_amount || 0) }),
    ...(row.type === 'goals' && {
        target: Number(row.target_amount || 0),
        current: Number(row.current_amount || 0)
    })
});

const mapTransactionFromDb = (row) => ({
    id: row.id,
    categoryId: row.category_id,
    amount: Number(row.amount || 0),
    date: row.transaction_date
});

const mapNoteFromDb = (row) => ({
    id: row.id,
    text: row.text,
    date: row.note_date,
    reminder: row.reminder_at || null,
    isTop: Boolean(row.is_top)
});

class RemoteStore {
    isEnabled() {
        return isSupabaseConfigured;
    }

    async loadAll() {
        if (!this.isEnabled()) return emptyData;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) {
            throw userError || new Error('Supabase user is not authenticated');
        }

        const userId = userData.user.id;
        const [categoriesResult, transactionsResult, notesResult] = await Promise.all([
            supabase.from('categories').select('*').eq('user_id', userId).order('created_at'),
            supabase.from('transactions').select('*').eq('user_id', userId).order('transaction_date', { ascending: false }),
            supabase.from('notes').select('*').eq('user_id', userId).order('note_date', { ascending: false })
        ]);

        const error = categoriesResult.error || transactionsResult.error || notesResult.error;
        if (error) throw error;

        const categories = { expenses: [], income: [], goals: [] };
        categoriesResult.data.forEach((row) => {
            categories[row.type]?.push(mapCategoryFromDb(row));
        });

        const transactions = { expenses: [], income: [], savings: [] };
        transactionsResult.data.forEach((row) => {
            transactions[row.type]?.push(mapTransactionFromDb(row));
        });

        return {
            categories,
            transactions,
            notes: notesResult.data.map(mapNoteFromDb)
        };
    }

    async saveCategories(categories) {
        if (!this.isEnabled()) return;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw userError || new Error('Supabase user is not authenticated');

        const userId = userData.user.id;
        const rows = [
            ...categories.expenses.map((category) => ({
                id: normalizeId(category.id),
                user_id: userId,
                type: 'expenses',
                name: category.name,
                limit_amount: category.limit || 0,
                target_amount: null,
                current_amount: null
            })),
            ...categories.income.map((category) => ({
                id: normalizeId(category.id),
                user_id: userId,
                type: 'income',
                name: category.name,
                limit_amount: null,
                target_amount: null,
                current_amount: null
            })),
            ...categories.goals.map((category) => ({
                id: normalizeId(category.id),
                user_id: userId,
                type: 'goals',
                name: category.name,
                limit_amount: null,
                target_amount: category.target || 0,
                current_amount: category.current || 0
            }))
        ];

        await supabase.from('categories').delete().eq('user_id', userId);
        if (rows.length) {
            const { error } = await supabase.from('categories').upsert(rows);
            if (error) throw error;
        }
    }

    async saveTransactions(transactions) {
        if (!this.isEnabled()) return;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw userError || new Error('Supabase user is not authenticated');

        const userId = userData.user.id;
        const rows = [
            ...transactions.expenses.map((transaction) => ({
                id: normalizeId(transaction.id),
                user_id: userId,
                category_id: normalizeId(transaction.categoryId),
                type: 'expenses',
                amount: transaction.amount,
                transaction_date: transaction.date
            })),
            ...transactions.income.map((transaction) => ({
                id: normalizeId(transaction.id),
                user_id: userId,
                category_id: normalizeId(transaction.categoryId),
                type: 'income',
                amount: transaction.amount,
                transaction_date: transaction.date
            }))
        ];

        await supabase.from('transactions').delete().eq('user_id', userId);
        if (rows.length) {
            const { error } = await supabase.from('transactions').upsert(rows);
            if (error) throw error;
        }
    }

    async saveNotes(notes) {
        if (!this.isEnabled()) return;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw userError || new Error('Supabase user is not authenticated');

        const userId = userData.user.id;
        const rows = notes.map((note) => ({
            id: normalizeId(note.id),
            user_id: userId,
            text: note.text,
            note_date: note.date,
            reminder_at: note.reminder || null,
            is_top: Boolean(note.isTop)
        }));

        await supabase.from('notes').delete().eq('user_id', userId);
        if (rows.length) {
            const { error } = await supabase.from('notes').upsert(rows);
            if (error) throw error;
        }
    }

    async clearAll() {
        if (!this.isEnabled()) return;

        const { data: userData, error: userError } = await supabase.auth.getUser();
        if (userError || !userData.user) throw userError || new Error('Supabase user is not authenticated');

        const userId = userData.user.id;
        await Promise.all([
            supabase.from('transactions').delete().eq('user_id', userId),
            supabase.from('categories').delete().eq('user_id', userId),
            supabase.from('notes').delete().eq('user_id', userId)
        ]);
    }
}

const remoteStore = new RemoteStore();
export default remoteStore;

